import styled from 'styled-components'
import mapboxgl from 'mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax
import { useContext, useEffect, useRef, useState } from 'react'
import 'mapbox-gl/dist/mapbox-gl.css'
import { LanguageContext } from 'components/MainComponent/MainComponent'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import Toggle from 'react-toggle'
import Legend from 'components/Legend'
import expositionArrow from '../../images/exposition-arrow.svg'
import GlobalContext from 'Contexts/GlobalContext'
import { de } from 'Globals/locale'
import { TbArrowsMove } from 'react-icons/tb'
import { IoCloseSharp } from 'react-icons/io5'

let userZooms = -5
const frMonths: Record<string, string> = {
  Januar: 'janvier',
  Februar: 'février',
  März: 'mars',
  April: 'avril',
  Mai: 'mai',
  Juni: 'juin',
  Juli: 'juillet',
  August: 'août',
  September: 'septembre',
  Oktober: 'octobre',
  November: 'novembre',
  Dezember: 'décembre',
}

mapboxgl.accessToken =
  'pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg'

// TOGGLE LAYER
function toggleLayer(map: mapboxgl.Map, layer: string, visible: boolean) {
  // console.log('toggleLayer', layer, visible)
  if (map.getLayer(layer)) {
    map.setLayoutProperty(layer, 'visibility', visible ? 'visible' : 'none')
  }
}
// TOGGLE DARK MODE
function toggleDMLayers(map: mapboxgl.Map, visible: boolean) {
  // toggleLayer(map, 'countries_dm', visible)
  // toggleLayer(map, 'countries_ch_dm', visible)
}
// SWITCH LANGUAGE
function switchLabelLang(map: mapboxgl.Map, layer: string, lang: string) {
  if (map.getLayer(layer)) {
    // console.log('switchLang', layer, lang)
    map.setLayoutProperty(layer, 'text-field', ['get', `name_${lang}`])
  }
}

// MAP
export const MapboxMap = ({
  isMobile,
  windowHeight,
  colorMode,
  tenant,
  setSelectedKmPop,
  trackEvent,
}: {
  isMobile: boolean
  windowHeight: number
  colorMode: 'light' | 'dark' | undefined
  tenant: any
  setSelectedKmPop: (a: number) => void
  trackEvent?: MicroFrontendProps['trackEvent']
}) => {
  const { locale } = useContext(GlobalContext) ?? { locale: de }
  const mapContainer = useRef<HTMLDivElement>(undefined as any)
  const map = useRef<mapboxgl.Map | null>(null)
  const [showDead, setShowDead] = useState(true)
  const [mapIsActive, setMapIsActive] = useState(false)
  const toggleNameLeft = locale.accidents
  const toggleNameRight = locale.deaths
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    if (!map.current) return
    switchLabelLang(map.current, 'settlement-major-label', tenant.lang)
    switchLabelLang(map.current, 'settlement-minor-label', tenant.lang)
    switchLabelLang(map.current, 'settlement-subdivision-label', tenant.lang)
  }, [tenant])

  useEffect(() => {
    if (!map.current) return
    toggleDMLayers(map.current, colorMode === 'dark')
  }, [colorMode])

  useEffect(() => {
    if (!map.current) return

    toggleLayer(map.current, 'not-deadly-tours', filter !== 'variants')
    toggleLayer(
      map.current,
      'exposition-not-deadly-tours',
      filter !== 'variants'
    )
    toggleLayer(map.current, 'deadly-tours', filter !== 'variants')
    toggleLayer(map.current, 'exposition-deadly-tours', filter !== 'variants')

    toggleLayer(map.current, 'not-deadly-variants', filter !== 'tours')
    toggleLayer(
      map.current,
      'exposition-not-deadly-variants',
      filter !== 'tours'
    )
    toggleLayer(map.current, 'deadly-variants', filter !== 'tours')
    toggleLayer(map.current, 'exposition-deadly-variants', filter !== 'tours')

    /*
    // OLD STUFF with TOGGLE: accidents <-> deaths
    toggleLayer(
      map.current,
      'not-deadly-tours',
      !showDead && filter !== 'variants'
    )
    toggleLayer(
      map.current,
      'exposition-not-deadly-tours',
      !showDead && filter !== 'variants'
    )
    toggleLayer(
      map.current,
      'not-deadly-variants',
      !showDead && filter !== 'tours'
    )
    toggleLayer(
      map.current,
      'exposition-not-deadly-variants',
      !showDead && filter !== 'tours'
    )
    toggleLayer(
      map.current,
      'not-deadly-others',
      !showDead && filter !== 'tours' && filter !== 'variants'
    )
    toggleLayer(
      map.current,
      'exposition-not-deadly-others',
      !showDead && filter !== 'tours' && filter !== 'variants'
    )

    toggleLayer(map.current, 'deadly-tours', showDead && filter !== 'variants')
    toggleLayer(
      map.current,
      'exposition-deadly-tours',
      showDead && filter !== 'variants'
    )
    toggleLayer(map.current, 'deadly-variants', showDead && filter !== 'tours')
    toggleLayer(
      map.current,
      'exposition-deadly-variants',
      showDead && filter !== 'tours'
    )
    toggleLayer(
      map.current,
      'deadly-others',
      showDead && filter !== 'tours' && filter !== 'variants'
    )
    toggleLayer(
      map.current,
      'exposition-deadly-others',
      showDead && filter !== 'tours' && filter !== 'variants'
    )
      */
  }, [showDead, filter])

  useEffect(() => {
    if (!map.current) return
    let _map = map.current

    if (mapIsActive) {
      _map.dragRotate.disable()
      _map.touchZoomRotate.enable()
      _map.touchZoomRotate.disableRotation()
      _map.touchPitch.enable()
      _map.scrollZoom.enable()
      _map.dragPan.enable()
    } else {
      mapContainer.current.scrollIntoView({ behavior: 'auto' })
      _map.dragRotate.disable()
      _map.touchZoomRotate.disable()
      _map.touchZoomRotate.disableRotation()
      _map.touchPitch.disable()
      _map.scrollZoom.disable()
      _map.dragPan.disable()
    }
  }, [mapIsActive])

  // INIT MAP
  useEffect(() => {
    if (map.current) return // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/ta-interaktiv/clq58qlmc00jo01qsek81bge2', // mapbox://styles/ta-interaktiv/cljo92emv00g501pmawhu4h9l
      center: isMobile ? [8.35, 46.65] : [8.4, 46.65],
      zoom: isMobile ? 5.9 : 7.75,
      maxZoom: 17,
      interactive: true,
      minZoom: 5,
      boxZoom: false,
      // maxBounds: [4.2, 44.2, 10.8, 49],
      projection: {
        name: 'equalEarth',
        center: [8.165588, 46.80194],
        parallels: [0, -90],
      } as any,
      // cooperativeGestures: window.innerWidth < 769 ? true : false,
      locale: {
        'ScrollZoomBlocker.CtrlMessage': locale.ctrl,
        'ScrollZoomBlocker.CmdMessage': locale.command,
        // 'TouchPanBlocker.Message': locale.touch,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // SET MAP FUNCTIONALITIES ON MAP LOAD
  useEffect(() => {
    if (!map.current) return

    map.current.on('load', () => {
      if (map.current !== null) {
        let _map = map.current
        // DISABLE MAP ROTATION
        _map.dragRotate.disable()
        _map.touchZoomRotate.disable()
        _map.touchZoomRotate.disableRotation()
        _map.touchPitch.disable()
        _map.scrollZoom.disable()
        _map.dragPan.disable()

        _map.setZoom(isMobile ? 5.9 : 7.75)
        _map.setCenter(isMobile ? [8.35, 46.65] : [8.4, 46.65])
        _map.setMaxBounds(
          isMobile ? [4.2, 44.2, 10.8, 49] : [4.2, 45.2, 10.8, 50]
        )

        // TOGGLE COLORMODE ON LOAD
        toggleDMLayers(_map, colorMode === 'dark')

        // TOGGLE DATA LAYERS ON LOAD
        toggleLayer(map.current, 'not-deadly-tours', filter !== 'variants')
        toggleLayer(
          map.current,
          'exposition-not-deadly-tours',
          filter !== 'variants'
        )
        toggleLayer(map.current, 'deadly-tours', filter !== 'variants')
        toggleLayer(
          map.current,
          'exposition-deadly-tours',
          filter !== 'variants'
        )

        toggleLayer(map.current, 'not-deadly-variants', filter !== 'tours')
        toggleLayer(
          map.current,
          'exposition-not-deadly-variants',
          filter !== 'tours'
        )
        toggleLayer(map.current, 'deadly-variants', filter !== 'tours')
        toggleLayer(
          map.current,
          'exposition-deadly-variants',
          filter !== 'tours'
        )

        /*
          // OLD STUFF with TOGGLE: accidents <-> deaths
          toggleLayer(
            map.current,
            'not-deadly-tours',
            !showDead && filter !== 'variants'
          )
          toggleLayer(
            map.current,
            'exposition-not-deadly-tours',
            !showDead && filter !== 'variants'
          )
          toggleLayer(
            map.current,
            'not-deadly-variants',
            !showDead && filter !== 'tours'
          )
          toggleLayer(
            map.current,
            'exposition-not-deadly-variants',
            !showDead && filter !== 'tours'
          )
          toggleLayer(
            map.current,
            'not-deadly-others',
            !showDead && filter !== 'tours' && filter !== 'variants'
          )
          toggleLayer(
            map.current,
            'exposition-not-deadly-others',
            !showDead && filter !== 'tours' && filter !== 'variants'
          )
          toggleLayer(
            map.current,
            'deadly-tours',
            showDead && filter !== 'variants'
          )
          toggleLayer(
            map.current,
            'exposition-deadly-tours',
            showDead && filter !== 'variants'
          )
          toggleLayer(
            map.current,
            'deadly-variants',
            showDead && filter !== 'tours'
          )
          toggleLayer(
            map.current,
            'exposition-deadly-variants',
            showDead && filter !== 'tours'
          )
          toggleLayer(
            map.current,
            'deadly-others',
            showDead && filter !== 'tours' && filter !== 'variants'
          )
          toggleLayer(
            map.current,
            'exposition-deadly-others',
            showDead && filter !== 'tours' && filter !== 'variants'
          )
        */

        switchLabelLang(map.current, 'settlement-major-label', tenant.lang)
        switchLabelLang(map.current, 'settlement-minor-label', tenant.lang)
        switchLabelLang(
          map.current,
          'settlement-subdivision-label',
          tenant.lang
        )

        // NAVIGATION CONTROLS
        mapContainer.current.getElementsByClassName(
          'mapboxgl-ctrl-top-right'
        )[0].innerHTML = ''
        _map.addControl(
          new mapboxgl.NavigationControl({ showCompass: false }),
          'top-right'
        )

        //add a popup when hovering over style features
        const popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: true,
          offset: [0, -5],
        })

        // detect if is hovering over a feature
        _map.on(
          window.innerWidth < 769 ? 'click' : 'mouseenter',
          [
            'not-deadly-tours',
            'not-deadly-variants',
            'not-deadly-others',
            'deadly-tours',
            'deadly-variants',
            'deadly-others',
          ],
          (e) => {
            const features = _map.queryRenderedFeatures(e.point)
            if (features.length > 0) {
              const feature = features[0]
              console.log('feature', feature)
              const layerId = feature.layer?.id // to make sure only 'exposition' layers are considered
              if (feature.properties && layerId?.includes('exposition')) {
                _map.getCanvas().style.cursor = 'pointer'
                const deDay = feature.properties.Datum_string.split('. ')[0]
                const deMonth =
                  feature.properties.Datum_string.split('. ')[1].split(' ')[0]
                let frMonth = frMonths[deMonth]
                const deYear =
                  feature.properties.Datum_string.split('. ')[1].split(' ')[1]

                popup
                  .setLngLat([
                    (feature.geometry as any).coordinates[0],
                    (feature.geometry as any).coordinates[1],
                  ])
                  .setHTML(
                    `<div class="popup">
                    <h3 class="popup__title">
                      ${feature.properties.Ort_fixed} ${
                      feature.properties.Kanton_fixed
                        ? '(' + feature.properties.Kanton_fixed + ')'
                        : ''
                    }
                    </h3>
                    <div class="popup__text">
                      <div class="meta">
                        <div class="date">
                          ${
                            tenant.lang === 'fr'
                              ? deDay + ' ' + frMonth + ' ' + deYear
                              : feature.properties.Datum_string
                          }
                        </div>
                        <div class="alt">
                          ${
                            !feature.properties.Höhe_fixed ||
                            feature.properties.Höhe_fixed === '' ||
                            feature.properties.Höhe_fixed === ' ' ||
                            feature.properties.Höhe_fixed === '0' ||
                            feature.properties.Höhe_fixed === 'NA' ||
                            feature.properties.Höhe_fixed === 'N/A' ||
                            feature.properties.Höhe_fixed === '-'
                              ? ''
                              : feature.properties.Höhe_fixed +
                                ' ' +
                                locale.meters
                          }
                        </div>
                      </div>

                      <div class="info">
                        <div class="pers"><span class='label'>${
                          locale.caught
                        }</span> ${feature.properties.erfasst_fixed}</div>
                        ${
                          feature.properties.verschuettet_fixed > 0
                            ? `<div class="bur"><span class='label'>${locale.buried}</span> ${feature.properties.verschuettet_fixed}</div>`
                            : ''
                        }
                        ${
                          feature.properties.gestorben_fixed > 0
                            ? `<div class="dead"><span class='label'>${locale.killed}</span> ${feature.properties.gestorben_fixed}</div>`
                            : ''
                        }
                      </div>
                      <div class="info">
                        <div class="act"><span class='label'>${
                          locale.location
                        }</span> ${
                      feature.properties.Aktivität_fixed === '' ||
                      feature.properties.Aktivität_fixed === 'unbestimmt'
                        ? locale.unknown
                        : feature.properties.Aktivität_fixed === '1'
                        ? locale.tourarea
                        : feature.properties.Aktivität_fixed === '2'
                        ? locale.variantsarea
                        : feature.properties.Aktivität_fixed === '1,2'
                        ? locale.toursorvariants
                        : feature.properties.Aktivität_fixed === '3'
                        ? locale.trafficarea
                        : feature.properties.Aktivität_fixed === '2,3'
                        ? locale.variantsortraffic
                        : feature.properties.Aktivität_fixed === '4'
                        ? locale.buildings
                        : feature.properties.Aktivität_fixed === '3,4'
                        ? locale.trafficorbuildings
                        : feature.properties.Aktivität_fixed
                    }</div>
                        <div class="expo"><span class='label'>${
                          locale.direction
                        }</span> <span class='icon'><img src=${expositionArrow} class=${
                      feature.properties.Expo === 'N'
                        ? 'no'
                        : feature.properties.Expo === 'NE'
                        ? 'ne'
                        : feature.properties.Expo === 'E'
                        ? 'ea'
                        : feature.properties.Expo === 'SE'
                        ? 'se'
                        : feature.properties.Expo === 'S'
                        ? 'so'
                        : feature.properties.Expo === 'SW'
                        ? 'sw'
                        : feature.properties.Expo === 'W'
                        ? 'we'
                        : feature.properties.Expo === 'NW'
                        ? 'nw'
                        : 'hidden'
                    } /></span>${
                      !feature.properties.Expo ||
                      feature.properties.Expo === '' ||
                      feature.properties.Expo === ' ' ||
                      feature.properties.Expo === 'NA' ||
                      feature.properties.Expo === '-'
                        ? locale.notdefined
                        : feature.properties.Expo === 'N'
                        ? locale.north
                        : feature.properties.Expo === 'NE'
                        ? locale.northeast
                        : feature.properties.Expo === 'E'
                        ? locale.east
                        : feature.properties.Expo === 'SE'
                        ? locale.southeast
                        : feature.properties.Expo === 'S'
                        ? locale.south
                        : feature.properties.Expo === 'SW'
                        ? locale.southwest
                        : feature.properties.Expo === 'W'
                        ? locale.west
                        : feature.properties.Expo === 'NW'
                        ? locale.northwest
                        : feature.properties.Expo
                    }</div>
                        <div class="info">
                          <div class="danger"><span class='label'>${
                            locale.danger
                          }</span>
                          </div>
                          <div class="danger">
                          ${
                            feature.properties.Gefahrenstufe_fixed ===
                              'unbestimmt' ||
                            feature.properties.Gefahrenstufe_fixed === '-' ||
                            feature.properties.Gefahrenstufe_fixed === 'txt' ||
                            feature.properties.Gefahrenstufe_fixed === '' ||
                            feature.properties.Gefahrenstufe_fixed === ' '
                              ? locale.nobulletin
                              : feature.properties.Gefahrenstufe_fixed === '1'
                              ? locale.stage1
                              : feature.properties.Gefahrenstufe_fixed === '-1'
                              ? locale.stage1 + ', ' + locale.notinzone
                              : feature.properties.Gefahrenstufe_fixed === '2'
                              ? locale.stage2
                              : feature.properties.Gefahrenstufe_fixed === '-2'
                              ? locale.stage2 + ', ' + locale.notinzone
                              : feature.properties.Gefahrenstufe_fixed === '3'
                              ? locale.stage3
                              : feature.properties.Gefahrenstufe_fixed === '-3'
                              ? locale.stage3 + ', ' + locale.notinzone
                              : feature.properties.Gefahrenstufe_fixed === '4'
                              ? locale.stage4
                              : feature.properties.Gefahrenstufe_fixed === '-4'
                              ? locale.stage4 + ', ' + locale.notinzone
                              : feature.properties.Gefahrenstufe_fixed === '5'
                              ? locale.stage5
                              : feature.properties.Gefahrenstufe_fixed === '-5'
                              ? locale.stage5 + ', ' + locale.notinzone
                              : feature.properties.Gefahrenstufe_fixed ===
                                '1↗︎2'
                              ? locale.stage1 +
                                ', ' +
                                locale.increasezone +
                                ' ' +
                                locale.stage2
                              : feature.properties.Gefahrenstufe_fixed ===
                                '1↗︎3'
                              ? locale.stage1 +
                                ', ' +
                                locale.increasezone +
                                ' ' +
                                locale.stage3
                              : feature.properties.Gefahrenstufe_fixed ===
                                '1↗︎4'
                              ? locale.stage1 +
                                ', ' +
                                locale.increasezone +
                                ' ' +
                                locale.stage4
                              : feature.properties.Gefahrenstufe_fixed ===
                                '1↗︎5'
                              ? locale.stage1 +
                                ', ' +
                                locale.increasezone +
                                ' ' +
                                locale.stage5
                              : feature.properties.Gefahrenstufe_fixed ===
                                '2↗︎3'
                              ? locale.stage2 +
                                ', ' +
                                locale.increasezone +
                                ' ' +
                                locale.stage3
                              : feature.properties.Gefahrenstufe_fixed ===
                                '2↗︎4'
                              ? locale.stage2 +
                                ', ' +
                                locale.increasezone +
                                ' ' +
                                locale.stage4
                              : feature.properties.Gefahrenstufe_fixed ===
                                '2↗︎5'
                              ? locale.stage2 +
                                ', ' +
                                locale.increasezone +
                                ' ' +
                                locale.stage5
                              : feature.properties.Gefahrenstufe_fixed ===
                                '3↗︎4'
                              ? locale.stage3 +
                                ', ' +
                                locale.increasezone +
                                ' ' +
                                locale.stage4
                              : feature.properties.Gefahrenstufe_fixed ===
                                '3↗︎5'
                              ? locale.stage3 +
                                ', ' +
                                locale.increasezone +
                                ' ' +
                                locale.stage5
                              : feature.properties.Gefahrenstufe_fixed ===
                                '4↗︎5'
                              ? locale.stage4 +
                                ', ' +
                                locale.increasezone +
                                ' ' +
                                locale.stage4
                              : feature.properties.Gefahrenstufe_fixed
                          }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>`
                  )
                  .addTo(_map)
              }
            } else {
              _map.getCanvas().style.cursor = ''
              popup.remove()
            }
          }
        )

        map.current.on(
          'mouseleave',
          [
            'not-deadly-tours',
            'not-deadly-variants',
            'not-deadly-others',
            'deadly-tours',
            'deadly-variants',
            'deadly-others',
          ],
          (e) => {
            popup.remove()
          }
        )
        map.current.on('zoom', () => {
          if (isMobile) {
            popup.remove()
          }
          if (userZooms > 0) {
            trackEvent?.('Interactions', {
              interactive_action: 'zoom',
              interactive_label: isMobile
                ? 'map_zoomed_mobile'
                : 'map_zoomed_desktop',
            })
            // console.log('Track Event:', userZooms)
          } else {
            userZooms++
          }
        })
        map.current.on('drag', () => {
          if (isMobile) {
            popup.remove()
          }
        })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map.current, isMobile, tenant])

  const toggleFilter = function (filter: string, filterState: string) {
    if (filter === filterState) {
      setFilter('all')
    } else {
      setFilter(filter)
    }
  }

  const Locales = useContext(LanguageContext)

  return (
    <>
      <MapBoxWrapper
        style={{
          height:
            window.innerWidth < 769
              ? windowHeight * 0.92 + 'px'
              : windowHeight * 0.87 + 'px',
        }}
        mapIsActive={isMobile && mapIsActive}
        windowHeight={
          window.innerWidth < 769 ? windowHeight * 0.92 : windowHeight * 0.87
        }
      >
        <ButtonRow
          style={{
            marginTop: isMobile && mapIsActive ? '50px' : 0,
          }}
        >
          <ActivateButton
            mapIsActive={mapIsActive}
            onClick={(e) => {
              var isAndroid = /(android)/i.test(navigator.userAgent)

              e.stopPropagation()
              e.bubbles = false
              if (map.current) {
                setTimeout(() => {
                  map.current && map.current.resize()
                }, 100)
                let body = document.body

                if (!mapIsActive) {
                  mapContainer.current?.scrollIntoView({
                    behavior: 'smooth',
                  })
                  if (isMobile) {
                    let headercontainer = Array.from(
                      document.getElementsByTagName('header')
                    )[0]?.parentElement?.parentElement
                    if (headercontainer) headercontainer.style.zIndex = '300'

                    !isAndroid && disableBodyScroll(mapContainer.current)
                    body.style.overflow = 'hidden'
                    // body.style.height = '100vh'
                  }
                } else {
                  if (isMobile) {
                    let headercontainer = Array.from(
                      document.getElementsByTagName('header')
                    )[0]?.parentElement?.parentElement
                    if (headercontainer) headercontainer.style.zIndex = '401'

                    !isAndroid && enableBodyScroll(mapContainer.current)
                    // body.style.overflow = 'unset'
                    // body.style.height = 'unset'
                  }
                  mapContainer.current?.scrollIntoView({
                    behavior: 'smooth',
                  })
                }
              }
              setMapIsActive(!mapIsActive)
            }}
          >
            <span className='icn'>
              {mapIsActive ? <IoCloseSharp /> : <TbArrowsMove />}{' '}
            </span>
            <span className='txt'>
              {mapIsActive ? locale.deactivateMap : locale.activateMap}
            </span>
          </ActivateButton>
        </ButtonRow>

        <MapboxMapRoot
          ref={mapContainer}
          className='map-container'
          style={{
            pointerEvents: mapIsActive ? 'all' : 'all',
            marginTop: isMobile && mapIsActive ? '60px' : 0,
          }}
        ></MapboxMapRoot>

        <ToggleButton>
          <div className='toggleWrapper'>
            {/*
            <div className='center'>
              {toggleNameLeft && (
                <div className={'label ' + (!showDead ? '' : 'off')}>
                  {' '}
                  {toggleNameLeft}
                </div>
              )}
              <div className='toggleContainer'>
                <Toggle
                  defaultChecked={showDead}
                  icons={false}
                  onChange={() => {
                    setShowDead(!showDead)
                  }}
                />
              </div>
              {toggleNameRight && (
                <div className={'label right ' + (showDead ? '' : 'off')}>
                  {toggleNameRight}
                </div>
              )}
            </div>
            */}

            <Legend
              legendItems={[toggleNameLeft, toggleNameRight]}
              itemType='circle'
              itemColors={['#FFE462', '#D3343F']}
            />

            <div className='filters'>
              <div
                className='choice'
                onClick={() => toggleFilter('tours', filter)}
              >
                <input
                  type='checkbox'
                  checked={filter === 'tours'}
                  onChange={() => toggleFilter('tours', filter)}
                />
                <span className='filter-label'>{locale.tours}</span>
              </div>
              <div
                className='choice'
                onClick={() => toggleFilter('variants', filter)}
              >
                <input
                  type='checkbox'
                  checked={filter === 'variants'}
                  onChange={() => toggleFilter('variants', filter)}
                />
                <span className='filter-label'>{locale.variants}</span>
              </div>
            </div>
          </div>
        </ToggleButton>
        <Source>
          <div>
            {locale.data} <a href={locale.link}>{locale.slf}</a>
          </div>
        </Source>
      </MapBoxWrapper>
    </>
  )
}

const BaseButton = styled.button`
  flex: 0 0 50px;
  height: 50px;
  border: 0;
  transition: transform 0.2s ease, max-width 0.2s ease;
  letter-spacing: 0.4px;
  pointer-events: all;
  overflow: hidden;
  border-radius: 4px;

  &:hover {
    cursor: pointer;
    transform: scale(1.05);
  }
  &:active {
    transform: scale(0.98);
  }
`

const MapBoxWrapper = styled.div<{
  mapIsActive: boolean
  windowHeight: number
}>`
  background-color: var(--site-background);
  z-index: 910;
  position: relative;
  width: 100%;
  margin-bottom: 60px;
  position: ${({ mapIsActive }) => (mapIsActive ? 'fixed' : 'relative')};
  top: 0;
  height: ${({ mapIsActive, windowHeight }) =>
    mapIsActive
      ? windowHeight + 'px !important'
      : undefined}; //  '100vh !important'
`

const Source = styled.div`
  padding-left: 1rem;
  div,
  a {
    color: var(--gray-3);
    font-size: 1.4rem;
    line-height: 2rem;
    margin-top: 0.3rem;
    margin-bottom: 0;
  }
  a {
    color: var(--blue-main);
    text-decoration: none;
  }
`

const ActivateButton = styled(BaseButton)<{ mapIsActive: boolean }>`
  font-size: 1.4rem;
  background-color: ${({ mapIsActive }) =>
    mapIsActive ? 'var(--red-main)' : 'var(--red-dark-1)'};
  color: #fff;
  min-width: 200px;
  margin-right: 0px;
  z-index: 604;
  .icn {
    font-size: 2rem;
    position: relative;
    top: 2px;
  }
  .txt {
    position: relative;
    top: -2px;
  }
`

const ButtonRow = styled.div`
  z-index: 1003;
  width: 100%;
  text-align: center;
  position: absolute;
  top: 40px;
  @media only screen and (max-width: 599px) {
    top: 30px;
  }

  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  pointer-events: none;
`
const MapboxMapRoot = styled.div`
  height: calc(100%) !important;
  position: relative;
  width: 100%;
  pointer-events: all;
  z-index: 1002;
  .hidden {
    display: none;
  }

  .mapboxgl-ctrl-top-right .mapboxgl-ctrl {
    @media only screen and (min-width: 716px) and (max-width: 1955px) {
      margin-right: 40px;
    }
  }

  .mapboxgl-popup {
    touch-action: none;
    color: var(--brandblue-main);
    font-family: var(--font-sans-serif);
    line-height: 0;
    margin-top: 5px;
    font-size: 1.4rem;
    line-height: 2.4rem;
    .popup__title {
      font-size: 1.7rem;
      border-bottom: 1px solid var(--gray-4);
      padding: 0.3rem 0 0.6rem;
      margin: 0 0 0.6rem;
    }
    .icon {
      img {
        width: 10px;
        height: auto;
        top: 0.35rem;
        position: relative;
        margin-right: 0.4rem;
      }
      .ne {
        transform: rotate(45deg);
      }
      .ea {
        transform: rotate(90deg);
        margin-right: 0.6rem;
      }
      .se {
        transform: rotate(135deg);
      }
      .so {
        transform: rotate(180deg);
      }
      .sw {
        transform: rotate(225deg);
      }
      .we {
        transform: rotate(270deg);
        margin-right: 0.6rem;
      }
      .nw {
        transform: rotate(315deg);
      }
      .hidden {
        display: none;
      }
    }
    .popup__text {
      font-size: 1.3rem;
      line-height: 2rem;
      .meta {
        color: var(--gray-2);
        font-size: 1.2rem;
        margin-top: -3px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-column-gap: 2rem;
        .date {
          text-align: left;
        }
        .alt {
          text-align: right;
        }
      }
      .info {
        margin-top: 1.2rem;
        letter-spacing: 0.3px;
        color: var(--gray-1);
        .label {
          color: var(--brandblue-main);
          font-size: 1.3rem;
          letter-spacing: normal;
          font-weight: 600;
          margin-right: 0.2rem;
        }
      }
    }
  }

  .mapboxgl-ctrl-attrib-inner {
    font-family: var(--font-sans-serif);
    font-size: 10px;
    padding: 0;
    line-height: 1.4em;
    background-color: var(--site-background);
    border-color: var(--site-background);
    a {
      color: var(--text-color);
    }
  }
  .mapboxgl-ctrl-attrib {
    background-color: var(--site-background);
    border-color: var(--site-background);
  }
`

const ToggleButton = styled.div`
  touch-action: none;
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  z-index: 1100;
  text-align: center;

  .toggleWrapper {
    padding: 0.8rem 1rem 0.8rem;
    @media only screen and (max-width: 599px) {
      padding: 0.5rem 0.6rem 0.5rem;
    }
    color: var(--text-color);
    font-family: var(--font-sans-serif);
    text-align: center;
    font-size: 0.9em;
    font-weight: 600;
    background: var(--site-background);
    border-radius: 4px;
    box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.075);

    // border-top: 1px solid #d6d7dd;
    display: inline-block;
    .toggleTitle {
      display: inline-block;
      width: 100%;
      margin-bottom: 1em;
    }
    .center {
      display: inline-block;
      margin-left: auto;
      margin-right: auto;
      width: 100%;

      display: flex;
      align-items: center;
      justify-content: center;
    }
    .label {
      float: left;
      text-align: right;
      padding-right: 1em;
      min-width: 90px;
      width: calc(50% - 25px);
      line-height: 1.3em;
      color: var(--text-color);
      &.off {
        color: #999baa;
      }
      &.right {
        padding: 0 0 0 1em;
        text-align: left;
      }
    }

    .toggleContainer {
      display: flex;
    }
  }
  // CSS FÜR DEN SCHALTER
  .react-toggle {
    touch-action: pan-x;
    display: inline-block;
    position: relative;
    cursor: pointer;
    background-color: transparent;
    border: 0;
    padding: 0;

    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    -webkit-tap-highlight-color: transparent;
  }

  .react-toggle-screenreader-only {
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }

  .react-toggle--disabled {
    cursor: not-allowed;
    opacity: 0.5;
    -webkit-transition: opacity 0.25s;
    transition: opacity 0.25s;
  }

  .react-toggle-track {
    width: 50px;
    height: 24px;
    padding: 0;
    border-radius: 30px;
    background-color: #c8c8d1;
    -webkit-transition: all 0.2s ease;
    -moz-transition: all 0.2s ease;
    transition: all 0.2s ease;
  }

  .react-toggle:hover:not(.react-toggle--disabled) .react-toggle-track {
    background-color: #000000;
  }

  .react-toggle--checked .react-toggle-track {
    background-color: #202346;
  }

  .react-toggle--checked:hover:not(.react-toggle--disabled)
    .react-toggle-track {
    background-color: #202346;
  }

  .react-toggle-track-check {
    position: absolute;
    width: 14px;
    height: 10px;
    top: 0px;
    bottom: 0px;
    margin-top: auto;
    margin-bottom: auto;
    line-height: 0;
    left: 8px;
    opacity: 0;
    -webkit-transition: opacity 0.25s ease;
    -moz-transition: opacity 0.25s ease;
    transition: opacity 0.25s ease;
  }

  .react-toggle--checked .react-toggle-track-check {
    opacity: 1;
    -webkit-transition: opacity 0.25s ease;
    -moz-transition: opacity 0.25s ease;
    transition: opacity 0.25s ease;
  }

  .react-toggle-track-x {
    position: absolute;
    width: 10px;
    height: 10px;
    top: 0px;
    bottom: 0px;
    margin-top: auto;
    margin-bottom: auto;
    line-height: 0;
    right: 10px;
    opacity: 1;
    -webkit-transition: opacity 0.25s ease;
    -moz-transition: opacity 0.25s ease;
    transition: opacity 0.25s ease;
  }

  .react-toggle--checked .react-toggle-track-x {
    opacity: 0;
  }

  .react-toggle-thumb {
    transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1) 0ms;
    position: absolute;
    top: 1px;
    left: 1px;
    width: 22px;
    height: 22px;
    border: 1px solid #4d4d4d;
    border-radius: 50%;
    background-color: #fafafa;

    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;

    -webkit-transition: all 0.25s ease;
    -moz-transition: all 0.25s ease;
    transition: all 0.25s ease;
  }

  .react-toggle--checked .react-toggle-thumb {
    left: 27px;
    border-color: #202346;
  }

  .react-toggle--focus .react-toggle-thumb {
    -webkit-box-shadow: 0px 0px 3px 2px #0099e0;
    -moz-box-shadow: 0px 0px 3px 2px #0099e0;
    box-shadow: 0px 0px 2px 3px #0099e0;
  }

  .react-toggle:active:not(.react-toggle--disabled) .react-toggle-thumb {
    -webkit-box-shadow: 0px 0px 5px 5px #0099e0;
    -moz-box-shadow: 0px 0px 5px 5px #0099e0;
    box-shadow: 0px 0px 5px 5px #0099e0;
  }

  .filters {
    font-weight: 400;
    font-size: 1.4rem;
    line-height: 2rem;
    @media only screen and (max-width: 599px) {
      font-size: 1.2rem;
      line-height: 1.8rem;
    }
    display: grid;
    grid-template-columns: 1fr 1fr;
    top: 0.2rem;
    position: relative;
    padding: 0.4rem 1rem 0 1.4rem;
    @media only screen and (max-width: 599px) {
      padding: 0 0.2rem 0.2rem 1rem;
    }
    .choice {
      text-align: left;
      padding: 0.2rem 0 0.4rem;
      display: grid;
      grid-template-columns: 2rem 14rem;
      cursor: default;
      input {
        display: flex;
        align-self: flex-start;
        position: relative;
      }
      .filter-label {
        padding-left: 0.3rem;
        position: relative;
        top: -1px;
        @media only screen and (max-width: 599px) {
          top: 2px;
        }
      }
    }
  }
`
